import { makeAutoObservable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { request } from '../api/api';
import { getUserPool } from '../api/cognito';
import UtilsStore from './stores/UtilsStore';
import AuthStore from './stores/AuthStore';
import FundsStore from './stores/FundsStore';
import WebSocketStore from './stores/WebSocketStore';
import ProjectsStore from './stores/ProjectsStore';
import ProfilerProjectsStore from './stores/ProfilerProjectsStore';
import OutreachStore from './stores/OutreachStore';
import AdminDashboardStore from './stores/AdminDashboardStore';
import ProfilerAccessStore from './stores/ProfilerAccessStore';
import ChatStore from './stores/ChatStore';

class Store {
  constructor() {
    makeAutoObservable(this);

    this.utilsStore = new UtilsStore(this);
    this.authStore = new AuthStore(this);
    this.fundsStore = new FundsStore(this);
    this.projectStore = new ProjectsStore(this);
    this.profilerProjectStore = new ProfilerProjectsStore(this);
    this.outreachStore = new OutreachStore(this);
    this.adminDashboardStore = new AdminDashboardStore(this);
    this.webSocketStore = new WebSocketStore(this);
    this.profilerAccessStore = new ProfilerAccessStore(this);
    this.chatStore = new ChatStore(this);
  }

  resetStore = () => {
    runInAction(() => {
      this.authStore.reset();
      this.fundsStore.reset();
      this.projectStore.reset();
      this.profilerProjectStore.reset();
      this.outreachStore.reset();
      this.adminDashboardStore.reset();
      this.webSocketStore.reset();
    });
  };

  getToken = async () => {
    return new Promise((resolve) => {
      const cognitoUser = getUserPool().getCurrentUser();
      if (cognitoUser) {
        cognitoUser.getSession((err, result) => {
          if (err) {
            return resolve(null);
          }

          return resolve(result?.idToken?.jwtToken);
        });
      } else {
        resolve(null);
      }
    });
  };

  makeRequest = async ({
    endpoint,
    body = {},
    headers = {},
    onSuccess = () => {},
    onError = () => {},
    onFinally = () => {}
  }) => {
    if (endpoint.withCognitoToken) {
      const token = await this.getToken();
      if (!token) {
        this.utilsStore.setIsSessionExpired(true);
        return;
      }

      headers['Authorization'] = token;
    }

    request({ endpoint, body, headers, params: endpoint.params })
      .then((response) =>
        runInAction(() => {
          // Legacy BE endpoints wrap responses in { success: true, data: {...} }.
          // The reveal/bulk-reveal/download-preview endpoints (Contact Enrichment,
          // TD-2100/3c/5b) return flat bodies and rely on HTTP status. Treat 2xx
          // as success when the legacy `success` field is absent. `httpStatus`
          // comes from the api.js envelope passthrough.
          const httpStatus = response?.httpStatus;
          const successByEnvelope = response?.success === true;
          const successByStatus =
            response?.success === undefined && httpStatus >= 200 && httpStatus < 300;
          if (successByEnvelope || successByStatus) {
            // For flat-body endpoints `response.data` is undefined; pass the whole
            // response (sans the synthetic `httpStatus`) so the hook sees the
            // semantic fields.
            const payload = successByEnvelope ? response.data : response;
            onSuccess(payload, httpStatus);
          } else {
            // Second arg is the full response object. Legacy callers read
            // `response.code` (and forward the whole object downstream, e.g. the
            // bulk-export limit-exceeded reopen flow); enrichment callers read
            // `response.httpStatus` to differentiate 402/410/502. The object
            // carries both — a strict superset of the pre-enrichment contract.
            onError((response?.errors?.errorMessages || []).join('\n'), response);
          }
          onFinally();
        })
      )
      .catch((err) => {
        if (err?.name === 'AbortError') {
          return;
        }

        console.error(err);

        runInAction(() => {
          if (!err.status) {
            // failed to fetch - very high probably network is down
            onError();
            onFinally();
          } else {
            // expected API calls to allways return 200 OK
            // this error should mean the gateway returned unauthtorized
            this.utilsStore.setIsSessionExpired(true);
          }
        });
      });
  };

  makeRequests = async ({
    requests = [],
    onSuccess = () => {},
    onError = () => {},
    onFinally = () => {}
  }) => {
    let token = null;

    if (requests.findIndex(({ endpoint }) => endpoint.withCognitoToken) !== -1) {
      token = await this.getToken();
      if (!token) {
        this.utilsStore.setIsSessionExpired(true);
        return;
      }
    }

    return Promise.all(
      requests.map(({ endpoint, body = {}, headers = {} }) => {
        if (endpoint.withCognitoToken) {
          headers['Authorization'] = token;
        }

        return request({ endpoint, body, headers, params: endpoint.params });
      })
    )
      .then((responses) =>
        runInAction(() => {
          const errorResponses = responses.filter((r) => !r.success);
          if (errorResponses.length) {
            onError(
              (errorResponses.map((r) => (r?.errors?.errorMessages || []).join('\n')) || []).join(
                '\n'
              )
            );
          } else {
            onSuccess(responses.map((r) => r.data));
          }
          onFinally();
        })
      )
      .catch((err) => {
        if (err?.name === 'AbortError') {
          return;
        }

        console.error(err);

        runInAction(() => {
          if (!err.status) {
            // failed to fetch - very high probably network is down
            onError();
            onFinally();
          } else {
            // expected API calls to allways return 200 OK
            // this error should mean the gateway returned unauthtorized
            this.utilsStore.setIsSessionExpired(true);
          }
        });
      });
  };
}

const store = new Store();
// Startup work runs here, not in a constructor: restoring the session and the
// LinkedIn callback are asynchronous, and every store exists by now.
void store.authStore.init();

const StoreContext = createContext(store);
const useStore = () => useContext(StoreContext);

export { Store, store, StoreContext, useStore };
